/**
 * ABTestingToggle — Story 11.5.
 *
 * Toggle visual "A/B/C testing ativo" no header da aba Variantes da campanha.
 * Decisão MVP pragmática: estado é IMPLÍCITO (sem schema change) baseado em
 * `count(variants WHERE active=true)`:
 *
 *   - 0 active   → OFF (campaign usa fallback `campaigns.meta_template_id`)
 *   - 1 active   → ON, mas sem rotation efetiva (equivalente ao fallback)
 *   - >=2 active → ON com rotation entre N variantes
 *
 * Click no toggle (quando ON com >=1 active):
 *   - Confirm modal "Desativar todas as N variantes?"
 *   - PATCH `/admin/campaigns/:id/templates/bulk` com `{active: false}`
 *   - Toast feedback
 *
 * Click no toggle quando OFF:
 *   - Mostra mensagem "Adicione pelo menos 2 variantes pra ativar A/B testing"
 *   - Não dispara mutation
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiClient } from '@/lib/api';

interface Props {
  campaignId: string;
  activeCount: number;
  totalCount: number;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function ABTestingToggle({ campaignId, activeCount, totalCount }: Props) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.patch<{ campaign_id: string; affected: number; active: boolean }>(
        `/admin/campaigns/${campaignId}/templates/bulk`,
        { active: false },
      ),
    onSuccess: (data) => {
      toast.success(`A/B testing desativado — ${data.affected} variante(s) desligada(s).`);
      setConfirmOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', campaignId, 'templates'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao desativar A/B testing.'));
    },
  });

  const isOn = activeCount > 0;
  const hasRotation = activeCount >= 2;

  let statusLabel: string;
  let statusClass: string;
  if (!isOn) {
    statusLabel = 'OFF — usando template padrão da campanha';
    statusClass = 'bg-muted text-muted-foreground';
  } else if (!hasRotation) {
    statusLabel = `ON — 1 variante (sem rotation efetiva)`;
    statusClass = 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  } else {
    statusLabel = `ON — rotation entre ${activeCount} variantes`;
    statusClass = 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
  }

  function handleToggleClick() {
    if (!isOn) {
      toast.info(
        totalCount === 0
          ? 'Adicione pelo menos 2 variantes pra ativar A/B testing.'
          : 'Variantes existem mas estão todas inativas — reative uma delas para retomar A/B.',
      );
      return;
    }
    setConfirmOpen(true);
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 px-3 py-2"
      data-testid="ab-testing-toggle"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">A/B testing:</span>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}
          data-testid="ab-testing-status"
        >
          {statusLabel}
        </span>
      </div>
      {isOn && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleToggleClick}
          data-testid="ab-testing-toggle-btn"
        >
          Desativar todas
        </Button>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desativar A/B testing?</DialogTitle>
            <DialogDescription>
              Desativa todas as {activeCount} variantes ativas — disparos passam a usar o template
              Meta padrão da campanha (<code>campaigns.meta_template_id</code>). Você pode reativar
              individualmente depois sem perder a configuração.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              data-testid="ab-testing-confirm-btn"
            >
              {mutation.isPending ? 'Desativando…' : 'Confirmar desativação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
